import React from "react"
import tw from "twin.macro"

import ModalExit from "@components/ModalExit"

export const ISIContent = ({ stickyIsi, scrollTo, headerRef }) => {
  const styles = {
    subheadings: tw`pt-4 font-extrabold leading-[1.25] lg:(leading-[1.20])`,
    paragraphLeading: tw`mt-2 leading-[1.1] lg:(leading-[1.2])`,
    lists: tw`list-disc list-outside pl-5`,
  }

  return (
    <div
      css={[tw`relative text-sm lg:(text-lg)`, stickyIsi && tw`bg-purple`]}
    >
      <div
        data-id={ stickyIsi ? "sticky-isi-header" : "isi-header"}
        css={[stickyIsi && tw`sticky top-0 pt-5 bg-purple md:pt-4 lg:pt-3`]}
        ref={headerRef}
      >
        <h2 tw="text-lg font-bold font-kanit leading-[1.25] lg:(text-xl leading-[1.20])">
          IMPORTANT SAFETY{" "}
          <span tw="whitespace-nowrap">
            INFORMATION
            <span
              css={[
                tw`transition-all opacity-0`,
                scrollTo && scrollTo !== "contraindications" && tw`opacity-100`,
              ]}
            >
              &nbsp;(cont'd)
            </span>
          </span>
        </h2>
      </div>
      <div data-id={ stickyIsi ? "sticky-isi-contraindications" : "isi-contraindications"}>
        <h3 css={styles.subheadings}>CONTRAINDICATIONS</h3>
        <p css={[styles.paragraphLeading, tw`mt-2`]}>
          DefenCath is contraindicated in patients with:
        </p>
        <ul css={[styles.paragraphLeading, styles.lists, tw`mt-2`]}>
          <li>Known heparin-induced thrombocytopenia (HIT).</li>
          <li>
            Known hypersensitivity to taurolidine, heparin or the citrate
            excipient (components of DefenCath), or pork products.
          </li>
        </ul>
      </div>
      <div data-id={stickyIsi ? "sticky-isi-warnings" : "isi-warnings"}>
        <h3 css={styles.subheadings}>WARNINGS AND PRECAUTIONS</h3>
        <ul css={[styles.paragraphLeading, styles.lists, tw`mt-2`]}>
          <li>
            <span tw="font-extrabold">
              Heparin-Induced Thrombocytopenia (HIT):
            </span>{" "}
            HIT was reported in patients using heparin, a component of
            DefenCath, as a catheter lock solution. If HIT occurs, discontinue
            DefenCath and institute appropriate supportive measures.
          </li>
          <li data-id={stickyIsi ? "sticky-isi-hyper" : "isi-hyper"}>
            <span tw="font-extrabold">Drug Hypersensitivity:</span> Drug
            hypersensitivity reactions have been reported in patients using
            heparin, a component of DefenCath, as a catheter lock solution. If a
            hypersensitivity reaction occurs, discontinue DefenCath and
            institute appropriate supportive measures.
          </li>
        </ul>
      </div>
      <div data-id={stickyIsi ? "sticky-isi-adverse" : "isi-adverse"}>
        <h3 css={styles.subheadings}>ADVERSE REACTIONS</h3>
        <div>
          <p tw="mt-4">
            The most frequently reported adverse reactions occurring in &ge;2%
            of patients using DefenCath as a CLS were hemodialysis catheter
            malfunction, hemorrhage/bleeding, nausea, vomiting, dizziness,
            musculoskeletal chest pain, and thrombocytopenia.
          </p>
          <div tw="mt-2 lg:(-tracking-[0.2px])">
            To report SUSPECTED ADVERSE REACTIONS, contact CorMedix Inc at{" "}
            <a
              tabIndex={stickyIsi ? -1 : null}
              href="tel:18444246345"
              css={[
                tw`whitespace-nowrap transition duration-300 hover:(underline text-light-purple)`,
                stickyIsi && tw`hover:(underline text-green)`,
              ]}
            >
              1-844-424-6345
            </a>{" "}
            or FDA at{" "}
            <a
              tabIndex={stickyIsi ? -1 : null}
              href="tel:18003321088"
              css={[
                tw`whitespace-nowrap transition duration-300 hover:(underline text-light-purple)`,
                stickyIsi && tw`hover:(underline text-green)`,
              ]}
            >
              1-800-FDA-1088
            </a>{" "}
            or{" "}
            <ModalExit
              tabIndex={stickyIsi ? -1 : null}
              triggerContent={<>www.fda.gov/medwatch</>}
              triggerStyles={[
                tw`underline transition duration-300 hover:(text-light-purple)`,
                stickyIsi && tw`hover:(text-green)`,
              ]}
              destinationUrl="https://www.fda.gov/safety/medwatch-fda-safety-information-and-adverse-event-reporting-program"
            />
            .
          </div>
          <div
            data-id={stickyIsi ? "sticky-isi-indications" : "isi-indications"}
          >
            <h2 tw="uppercase text-lg font-bold font-kanit leading-[1.25] mt-2 lg:(mt-4 text-xl leading-[1.20])">
              Indications and Usage
            </h2>
            <p css={[styles.paragraphLeading, tw`mt-2 lg:(mt-4)`]}>
              <span tw="font-extrabold">LIMITED POPULATION:</span> DefenCath
              <sup>&reg;</sup> is indicated to reduce the incidence of
              catheter-related bloodstream infections (CRBSI) in adult patients
              with kidney failure receiving chronic hemodialysis (HD) through a
              central venous catheter (CVC). This drug is indicated for use in a
              limited and specific population of patients.
            </p>
            <p css={[styles.paragraphLeading, tw`mt-2`]}>
              <span tw="font-extrabold underline decoration-[1.5px] md:(decoration-2)">
                Limitations of Use
              </span>
              <br />
              The safety and effectiveness of DefenCath have not been
              established for use in populations other than adult patients with
              kidney failure receiving chronic HD through a CVC.
            </p>
          </div>
          <p
            css={[
              styles.paragraphLeading,
              tw`font-semibold font-kanit tracking-[0.005rem] mt-2.5 md:(mt-3.5) lg:(leading-[0.9])`,
            ]}
          >
            Please see the full{" "}
            <a
              tabIndex={stickyIsi ? -1 : null}
              href="/pdfs/prescribing-information.pdf"
              target="_blank"
              rel="noopener noreferrer"
              css={[
                tw`underline transition duration-300 hover:(text-light-purple)`,
                stickyIsi && tw`hover:(text-green)`,
              ]}
              tw="underline "
            >
              Prescribing Information
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  )
}
